import { BucketingError, ContextInterface as ConvertContextInterface, EntityType, RuleError } from "@convertcom/js-sdk";
import type { ConfigExperience as ConvertExperience } from "@convertcom/js-sdk-types/lib/types";
import { Nullable, Optional } from "sonobello.utilities.react";
import TelemetryClient from "../../Telemetry/compositions/TelemetryClient";

import { IActiveExperiment } from "../Components/ExperimentContextLoader";
import ConvertExperimentConfiguration from "../types/ConvertExperimentConfiguration";
import { ConvertMalformedExperienceEvent } from "../types/ConvertTelemetryEvents";
import ExperimentConfiguration, { IExperimentConfiguration } from "../types/ExperimentConfiguration";
import ExperimentKey from "../types/ExperimentKey";
import IExperimentContextProps, { DefaultExperimentConfiguration } from "../types/IExperimentContextProps";
import EnvironmentConfiguration from "../../types/EnvironmentConfiguration";

interface IConvertDotcomExperimentIdentity {
  /** The primary identifier of the experiment in the OBX system. */
  id: string;
  /** The experience entity received from Convert.com which is associated with the experiment. */
  experience: ConvertExperience & { key: string };
}

const hasExperienceKey = (experience: Optional<ConvertExperience>): experience is ConvertExperience & { key: string } =>
  typeof experience?.key === "string" && experience.key.length > 0;

const isConvertVariationResult = (
  result: ReturnType<ConvertContextInterface["runExperience"]>
): result is Exclude<ReturnType<ConvertContextInterface["runExperience"]>, RuleError | BucketingError> =>
  typeof result === "object" && result !== null;

const getExperimentIdentities = (
  userContext: ConvertContextInterface,
  activeExperiments: IActiveExperiment[]
): IConvertDotcomExperimentIdentity[] => {
  const experimentIdentities: IConvertDotcomExperimentIdentity[] = [];
  activeExperiments.forEach(({ id, externalId }) => {
    const experience = userContext.getConfigEntityById(
      externalId,
      EntityType.EXPERIENCE
    ) as Optional<ConvertExperience>;
    if (!hasExperienceKey(experience)) {
      TelemetryClient.traceEvent(
        new ConvertMalformedExperienceEvent(id, externalId, (experience as Optional<Record<string, unknown>>) || null)
      );
      console.warn(`Convert.com experience '${externalId}' is missing a key. Skipping experiment '${id}'.`);
      return;
    }
    experimentIdentities.push({ id, experience });
  });
  return experimentIdentities;
};

/** The collection of goals that can be achieved by a Covert.com experiment. */
enum ConvertDotComGoals {
  /** The goal of booking a consultation at the end of a user experience. */
  BookedConsultation = "book-a-consultation"
}

/** Prototype: hand-rolled REST call to a second Convert project that has no SDK support.
 * @remarks Visitor id cannot currently be recovered from cookies, so it is left unset. See docs/convert-web-sdk.md.
 */
const trackWebProjectBookedAppointmentGoal = (): void => {
  const { accountId, webGoalTracking } = EnvironmentConfiguration.convertDotCom;
  fetch(`https://metrics.convertexperiments.com/v1/track/${accountId}/${webGoalTracking.projectId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      accountId,
      projectId: webGoalTracking.projectId,
      visitors: [
        {
          visitorId: undefined,
          events: [{ eventType: "conversion", data: { goalId: webGoalTracking.goalId } }]
        }
      ]
    })
  }).catch(e => console.error("Failed to record booked appointment conversion for Convert web project.", e));
};

/**
 * An {@link IExperimentContextProps} context for Convert.com.
 * @remarks Experiments are retrieved from Convert.com by their 'key', which is set by the system to be the hyphenated
 * name of the experiment.
 */
class ConvertExperimentContextProps implements IExperimentContextProps {
  /** The connection to the convert.com sdk contextualized for the current user. */
  private readonly userContext: ConvertContextInterface;
  /** The roster of live experiments that the user may experience. */
  private readonly experiments: Record<string, ExperimentConfiguration>;
  private readonly experimentIdentities: IConvertDotcomExperimentIdentity[];

  constructor(userContext: ConvertContextInterface, activeExperiments: IActiveExperiment[]) {
    try {
      this.experimentIdentities = getExperimentIdentities(userContext, activeExperiments);
    } catch (e) {
      console.warn("Failed to retrieve active experiments from Convert.com. Using default variations.", e);
      this.experimentIdentities = [];
    }
    this.experiments = {} as Record<string, ExperimentConfiguration>;
    this.userContext = userContext;
  }

  readonly getExperimentConfiguration = (experimentKey: ExperimentKey): IExperimentConfiguration => {
    if (this.experiments[experimentKey]) return this.experiments[experimentKey];

    const matchedExperimentIdentity = this.experimentIdentities.find(e => e.experience.key === experimentKey) || null;

    if (matchedExperimentIdentity) {
      try {
        const runExperienceResult = this.userContext.runExperience(matchedExperimentIdentity.experience.key, {
          locationProperties: { screen: "home" }
        });

        if (!isConvertVariationResult(runExperienceResult)) {
          console.warn(
            `Using default configuration for experiment '${experimentKey}' due to invalid variation result.`
          );
          this.experiments[experimentKey] = new DefaultExperimentConfiguration();
          return this.experiments[experimentKey];
        }
        this.experiments[experimentKey] = new ConvertExperimentConfiguration(
          matchedExperimentIdentity.id,
          runExperienceResult
        );
      } catch (e) {
        console.error(e);
        console.warn(`Using default configuration for experiment '${experimentKey}'.`);
        this.experiments[experimentKey] = new DefaultExperimentConfiguration();
      }
    } else {
      console.warn(`Experiment '${experimentKey}' not found in Convert.com. Using default configuration.`);
      this.experiments[experimentKey] = new DefaultExperimentConfiguration();
    }
    return this.experiments[experimentKey];
  };

  readonly recordBookedAppointment = () => {
    try {
      this.userContext.trackConversion(ConvertDotComGoals.BookedConsultation);
    } catch (e) {
      console.error("Failed to record booked appointment conversion.", e);
    }

    try {
      trackWebProjectBookedAppointmentGoal();
    } catch (e) {
      console.error("Failed to record booked appointment conversion for Convert web project.", e);
    }
  };
}

export default ConvertExperimentContextProps;
